import { createDocument, deleteCreatedDocument } from '../documents/document-service'
import { getSupabaseClient } from '../../lib/supabase'
import type { Task, TaskEvidenceStage, TaskPriority, TaskStatus, TaskWorkspace } from './task-types'

export async function getTaskWorkspace(propertyId: string, organizationId: string): Promise<TaskWorkspace> {
  const supabase = getSupabaseClient()
  const [tasks, assessment, actions, questions, documents, members] = await Promise.all([
    supabase.from('tasks').select('*').eq('property_id', propertyId).order('created_at', { ascending: false }),
    supabase.from('readiness_assessments').select('id').eq('property_id', propertyId).maybeSingle(),
    supabase.from('readiness_actions').select('id,question_id,title'),
    supabase.from('readiness_questions').select('id,prompt'),
    supabase.from('documents').select('id,title,current_version_number').eq('property_id', propertyId).eq('status', 'active'),
    supabase.from('organization_members').select('user_id').eq('organization_id', organizationId),
  ])
  for (const result of [tasks, assessment, actions, questions, documents, members]) if (result.error) throw result.error

  const taskRows = tasks.data ?? []
  const memberRows = members.data ?? []
  const taskIds = taskRows.map((task) => task.id)
  const [comments, activity, evidence, profiles] = await Promise.all([
    taskIds.length ? supabase.from('task_comments').select('*').in('task_id', taskIds).order('created_at') : Promise.resolve({ data: [], error: null }),
    taskIds.length ? supabase.from('task_activity').select('*').in('task_id', taskIds).order('created_at', { ascending: false }) : Promise.resolve({ data: [], error: null }),
    taskIds.length ? supabase.from('task_evidence').select('*').in('task_id', taskIds).order('created_at', { ascending: false }) : Promise.resolve({ data: [], error: null }),
    supabase.from('profiles').select('id,display_name').in('id', memberRows.map((member) => member.user_id)),
  ])
  if (comments.error) throw comments.error
  if (activity.error) throw activity.error
  if (evidence.error) throw evidence.error
  if (profiles.error) throw profiles.error

  const evidenceRows = evidence.data ?? []
  const evidenceDocumentIds = evidenceRows.map((item) => item.document_id)
  const versions = evidenceDocumentIds.length
    ? await supabase.from('document_versions').select('document_id,version_number,file_name,mime_type,storage_path').in('document_id', evidenceDocumentIds)
    : { data: [], error: null }
  if (versions.error) throw versions.error
  const currentDocuments = documents.data ?? []
  const evidenceItems = evidenceRows.flatMap((item) => {
    const document = currentDocuments.find((candidate) => candidate.id === item.document_id)
    const version = (versions.data ?? []).find((candidate) => candidate.document_id === item.document_id && candidate.version_number === document?.current_version_number)
    return document && version ? [{ ...item, document_title: document.title, file_name: version.file_name, mime_type: version.mime_type, storage_path: version.storage_path }] : []
  })

  return {
    tasks: taskRows,
    comments: comments.data ?? [],
    activity: activity.data ?? [],
    evidence: evidenceItems,
    assessmentId: assessment.data?.id ?? null,
    actions: actions.data ?? [],
    questions: questions.data ?? [],
    documents: currentDocuments.map(({ id, title }) => ({ id, title })),
    members: memberRows.map((member) => ({
      userId: member.user_id,
      label: (profiles.data ?? []).find((profile) => profile.id === member.user_id)?.display_name || `Member ${member.user_id.slice(0, 8)}`,
    })),
  }
}

export async function createTask(input: { organizationId: string; propertyId: string; assessmentId: string | null; title: string; description: string; priority: TaskPriority; assignedTo: string; dueDate: string; actionId: string; questionId: string; userId: string }) {
  const { error } = await getSupabaseClient().from('tasks').insert({
    organization_id: input.organizationId, property_id: input.propertyId,
    assessment_id: input.actionId || input.questionId ? input.assessmentId : null,
    title: input.title.trim(), description: input.description.trim() || null, priority: input.priority,
    assigned_to: input.assignedTo || null, due_date: input.dueDate || null,
    readiness_action_id: input.actionId || null, question_id: input.questionId || null, created_by: input.userId,
  })
  if (error) throw error
}

export async function updateTask(taskId: string, values: { status?: TaskStatus; priority?: TaskPriority; assigned_to?: string | null; due_date?: string | null; closure_document_id?: string | null }) {
  const { error } = await getSupabaseClient().from('tasks').update(values).eq('id', taskId)
  if (error) throw error
}

export async function addTaskComment(taskId: string, body: string, userId: string) {
  const { error } = await getSupabaseClient().from('task_comments').insert({ task_id: taskId, body: body.trim(), created_by: userId })
  if (error) throw error
}

export async function createTaskEvidence(input: {
  task: Task
  assessmentId: string | null
  stage: TaskEvidenceStage
  caption: string
  file: File
  userId: string
}): Promise<void> {
  const supabase = getSupabaseClient()
  const title = input.caption.trim() || `${input.stage === 'completion' ? 'Completion' : input.stage === 'before' ? 'Before work' : 'Work progress'}: ${input.task.title}`
  const created = await createDocument({
    organizationId: input.task.organization_id,
    propertyId: input.task.property_id,
    assessmentId: input.task.assessment_id ?? input.assessmentId,
    questionId: input.task.question_id,
    title,
    category: input.file.type.startsWith('image/') ? 'photo' : 'other',
    expiresOn: '',
    userId: input.userId,
    file: input.file,
  })
  const { error: evidenceError } = await supabase.from('task_evidence').insert({
    organization_id: input.task.organization_id,
    property_id: input.task.property_id,
    task_id: input.task.id,
    document_id: created.document.id,
    stage: input.stage,
    caption: input.caption.trim() || null,
    created_by: input.userId,
  })
  if (evidenceError) {
    await deleteCreatedDocument(created.document.id, created.version.storage_path)
    throw evidenceError
  }
  if (input.stage === 'completion' && !input.task.closure_document_id) {
    const { error: closureError } = await supabase.from('tasks').update({ closure_document_id: created.document.id }).eq('id', input.task.id)
    if (closureError) {
      await supabase.from('task_evidence').delete().eq('document_id', created.document.id)
      await deleteCreatedDocument(created.document.id, created.version.storage_path)
      throw closureError
    }
  }
}
